import React from 'react';
import { Menu, X, ChevronRight, Phone, Facebook, Instagram } from 'lucide-react';
import BrandLogo from '../ui/BrandLogo';
import { HalloweenCobweb, HalloweenSpider } from '../ui/HalloweenDecor';

const Navbar = ({ isScrolled, isMenuOpen, setIsMenuOpen, scrollToSection }) => {
  return (
    <>
      {/* Navigation */}
      <nav className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${isScrolled
        ? 'border-white/10 bg-concept-dark/90 shadow-lg shadow-black/10 backdrop-blur-xl'
        : 'border-white/[0.06] bg-concept-dark/35 backdrop-blur-md'
        }`}>
        {/* Subtle decorative cobweb in top-right */}
        <HalloweenCobweb corner="top-right" size="sm" className="absolute top-0 right-0 z-0 opacity-40 hidden sm:block" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <button type="button" aria-label="Concept USA — strona główna" className="group rounded-xl text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-300" onClick={() => scrollToSection('home')}>
              <BrandLogo />
            </button>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8 relative">
              {[
                { id: 'home', label: 'Start' },
                { id: 'about', label: 'O nas' },
                { id: 'process', label: 'Jak to działa' },
                { id: 'portfolio', label: 'Portfolio' },
                { id: 'order', label: 'Zamów auto' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="relative px-2 py-2 text-sm font-medium tracking-wide text-slate-300 transition-colors duration-300 group hover:text-white hover:bg-transparent border-0"
                >
                  {item.label}
                  <span className="nav-link-glow absolute -bottom-1 left-0 h-[2px] w-0 transition-all duration-300 group-hover:w-full" />
                </button>
              ))}

              {/* Contact button */}
              <button
                onClick={() => scrollToSection('contact')}
                className="neon-cta relative rounded-full border border-concept-red/70 bg-concept-red px-5 py-2.5 font-semibold text-white transition-colors duration-300 hover:bg-red-700"
              >
                <span className="flex items-center gap-2">
                  <Phone size={16} />
                  Kontakt
                </span>
              </button>

              {/* Little playful hanging spider swaying beneath the navbar */}
              <HalloweenSpider
                hanging={true}
                threadLength={24}
                size="sm"
                className="hidden xl:block absolute -bottom-8 -right-3 pointer-events-auto"
              />
            </div>

            {/* Mobile Menu Button */}
            <button
              type="button"
              aria-label={isMenuOpen ? 'Zamknij menu' : 'Otwórz menu'}
              aria-expanded={isMenuOpen}
              className="md:hidden text-slate-200 hover:text-white transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`md:hidden fixed inset-0 z-[100] transition-all duration-500 ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
          }`}
      >
        <div className="absolute inset-0 bg-concept-dark/95 backdrop-blur-xl">
          <HalloweenCobweb corner="top-right" size="md" className="absolute top-0 right-0 opacity-35" />
          <HalloweenCobweb corner="bottom-left" size="sm" className="absolute bottom-0 left-0 opacity-25" />
        </div>

        <div className={`relative h-full flex flex-col transition-all duration-500 ${isMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'
          }`}>
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-white/5">
            <BrandLogo />
            <button
              onClick={() => setIsMenuOpen(false)}
              className="w-12 h-12 flex items-center justify-center bg-white/5 hover:bg-concept-red text-white rounded-full transition-all duration-300"
            >
              <X size={24} />
            </button>
          </div>

          {/* Menu items */}
          <div className="flex-1 flex flex-col justify-center px-6 space-y-4">
            {[
              { id: 'home', label: 'Start' },
              { id: 'about', label: 'O nas' },
              { id: 'process', label: 'Jak to działa' },
              { id: 'portfolio', label: 'Portfolio' },
              { id: 'order', label: 'Zamów auto' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  scrollToSection(item.id);
                  setIsMenuOpen(false);
                }}
                className="group flex items-center gap-4 py-4 px-6 text-xl font-medium rounded-xl transition-all duration-300 border border-transparent bg-white/5 hover:bg-white/10"
              >
                <span className="text-slate-300 group-hover:text-white">{item.label}</span>
                <ChevronRight size={20} className="ml-auto opacity-0 group-hover:opacity-100 transition-all text-concept-red duration-300 group-hover:translate-x-1" />
              </button>
            ))}

            <button
              onClick={() => {
                scrollToSection('contact');
                setIsMenuOpen(false);
              }}
              className="neon-cta flex items-center justify-center gap-3 py-5 px-6 mt-4 text-xl font-bold bg-concept-red hover:bg-concept-red-dark text-white rounded-xl shadow-lg shadow-concept-red/20 transition-all duration-300"
            >
              <Phone size={24} />
              <span>Kontakt</span>
            </button>
          </div>

          {/* Footer with social links */}
          <div className="p-6 border-t border-white/5">
            <div className="flex justify-center gap-6">
              <a href="https://www.facebook.com/conceptsamochodyzusa" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
                <Facebook size={28} />
              </a>
              <a href="https://www.instagram.com/concept_samochody_z_usa" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
                <Instagram size={28} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
